import React, { useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import PersonalDetailsForm from './PersonalDetailsForm';
import ContactDetailsForm from './ContactDetailsForm';
import StatusRadioGroup from './StatusRadioGroup';
import { formatDateCST, formatTimeCST } from '../../../../utlis';

const CreateEditForm = ({
  initialData,
  onSubmit,
  closeModal,
  jobs,
  relations,
  managers,
  refSalesLoading,
  users,
  usersLoading,
  isSubmitting,
  apiFieldErrors,
  submitButtonLabel,
}) => {
  const methods = useForm({
    defaultValues: {
      salesStatus: 'active',
    },
    mode: 'onChange',
  });

  const { reset, formState, setError, clearErrors } = methods;
  const { isDirty, isValid } = formState;

  // Use useEffect to update form values when initialData changes
  useEffect(() => {
    if (initialData) {
      // Reset form with initialData as the new default values
      reset(initialData, {
        keepDirty: false,
        keepDirtyValues: false,
      });
    }
  }, [initialData, reset]);

  // Set form errors when API returns field-level errors
  useEffect(() => {
    if (apiFieldErrors && Object.keys(apiFieldErrors).length > 0) {
      // Set each field error using react-hook-form's setError
      Object.entries(apiFieldErrors).forEach(([fieldName, message]) => {
        setError(fieldName, {
          type: 'api',
          message: message,
        });
      });
    }
  }, [apiFieldErrors, setError]);

  // Clear API errors when user starts editing the errored fields
  const handleFieldChange = (fieldName) => {
    if (apiFieldErrors && apiFieldErrors[fieldName]) {
      clearErrors(fieldName);
    }
  };

  // Determine if update button should be enabled
  const isUpdateMode = initialData && initialData._id;
  const isButtonDisabled = isSubmitting || (isUpdateMode && (!isDirty || !isValid));

  const handleFormSubmit = (formData) => {
    onSubmit(formData);
  };

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleFormSubmit)}
        className="flex flex-col gap-6 text-text-hint"
      >
        <PersonalDetailsForm
          jobs={jobs}
          managers={managers}
          refSalesLoading={refSalesLoading}
          users={users}
          usersLoading={usersLoading}
          apiFieldErrors={apiFieldErrors}
          initialLoginId={initialData?.loginId}
        />

        <ContactDetailsForm relations={relations} apiFieldErrors={apiFieldErrors} />

        <div className="grid grid-cols-3 gap-5">
          <div className="flex flex-col col-span-3">
            <label htmlFor="notes" className="text-text-hint mb-1">
              Notes
            </label>
            <textarea
              {...methods.register('notes')}
              placeholder="Notes"
              className="outline-none border-2 border-secondary-500 rounded-lg"
              rows={4}
            />
          </div>
        </div>

        <StatusRadioGroup />

        {/* Display-only audit fields for Edit Mode */}
        {isUpdateMode && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-1.5 md:gap-2 mt-4 pt-4 border-t border-gray-200">
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Created By</label>
              <p className="text-sm text-gray-600">{initialData?.created_by || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Date Created</label>
              <p className="text-sm text-gray-600">
                {initialData?.created ? formatDateCST(initialData.created) : 'N/A'}
              </p>
              {initialData?.created && (
                <p className="text-xs text-gray-500">{formatTimeCST(initialData.created)}</p>
              )}
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Updated By</label>
              <p className="text-sm text-gray-600">{initialData?.updated_by || 'N/A'}</p>
            </div>
            <div className="flex flex-col space-y-1">
              <label className="text-text-hint text-xs">Last Updated</label>
              <p className="text-sm text-gray-600">
                {initialData?.updated ? formatDateCST(initialData.updated) : 'N/A'}
              </p>
              {initialData?.updated && (
                <p className="text-xs text-gray-500">{formatTimeCST(initialData.updated)}</p>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-between p-6 mt-6">
          <button
            type="button"
            onClick={closeModal}
            className={`border border-secondary-800 text-secondary-800 py-2 px-5 rounded-full ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isSubmitting}
          >
            Close
          </button>
          <button
            type="submit"
            className={`bg-secondary-700 text-text-light py-2 px-5 rounded-full flex items-center gap-2 ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isButtonDisabled}
          >
            {isSubmitting && (
              <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            )}
            {isSubmitting ? 'Saving...' : (submitButtonLabel || 'Save')}
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateEditForm;
